import { useState, useRef, useEffect } from "react";
import { Reveal, SectionLabel } from "../Reveal";
import { GoldSeal } from "../effects/GoldSeal";
import { HOST, BIO_PARAGRAPHS, ASSETS } from "../../content";

const Corner = ({ pos }) => (
  <span className={`absolute w-5 h-5 border-[#FE0000] ${pos}`} aria-hidden="true" />
);

export const HostSection = () => {
  const [hovered, setHovered] = useState(false);
  const [mouse, setMouse] = useState({ x: 0, y: 0 });
  const [isDesktop, setIsDesktop] = useState(true);
  const panelRef = useRef(null);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px) and (hover: hover)");
    setIsDesktop(mq.matches);
    const fn = (e) => setIsDesktop(e.matches);
    mq.addEventListener("change", fn);
    return () => mq.removeEventListener("change", fn);
  }, []);

  const onMove = (e) => {
    const rect = panelRef.current.getBoundingClientRect();
    setMouse({
      x: ((e.clientX - rect.left) / rect.width - 0.5) * 2,
      y: ((e.clientY - rect.top) / rect.height - 0.5) * 2,
    });
  };

  return (
    <section id="host" className="py-28 md:py-36 px-6 sm:px-10 lg:px-16 xl:px-24 bg-white/[0.015]" data-testid="host-section">
      <Reveal><SectionLabel num="005" text="YOUR HOST" /></Reveal>
      <div className="grid lg:grid-cols-2 gap-14 lg:gap-20 mt-4">
        <Reveal>
          <div
            ref={panelRef}
            onMouseEnter={() => isDesktop && setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            onMouseMove={isDesktop ? onMove : undefined}
            className={`relative w-full max-w-md mx-auto lg:mx-0 rounded-lg bg-[#182f56] border border-white/10 overflow-hidden ${!isDesktop ? "pulse-border" : ""}`}
            style={{ aspectRatio: "4/5" }}
            data-testid="host-portrait-panel"
          >
            <Corner pos="top-3 left-3 border-t border-l" />
            <Corner pos="top-3 right-3 border-t border-r" />
            <Corner pos="bottom-3 left-3 border-b border-l" />
            <Corner pos="bottom-3 right-3 border-b border-r" />
            <div
              className="absolute inset-0 transition-opacity duration-500 pointer-events-none"
              style={{
                opacity: hovered ? 1 : 0,
                background: "radial-gradient(circle at 50% 55%, rgba(254,0,0,0.22) 0%, rgba(254,0,0,0.05) 45%, transparent 70%)",
              }}
            />
            {isDesktop && <GoldSeal hovered={hovered} mouse={mouse} />}
            <div
              className="absolute inset-0 transition-opacity duration-500"
              style={{ opacity: hovered ? 0 : 1 }}
            >
              <img src={ASSETS.headshot} alt={HOST.name} className="absolute inset-0 w-full h-full object-cover" data-testid="host-headshot" />
            </div>
          </div>
          <div className="mt-6 max-w-md mx-auto lg:mx-0 text-center lg:text-left">
            <div className="serif text-2xl md:text-3xl font-black uppercase tracking-tight text-white" data-testid="host-full-name">
              {HOST.fullName}
            </div>
            <div className="mono text-[13px] tracking-[0.3em] text-[#FFC631] mt-2">{HOST.company}</div>
          </div>
        </Reveal>
        <div>
          <Reveal delay={0.05}>
            <h2 className="serif font-black uppercase tracking-tight text-3xl md:text-4xl text-white">
              GUIDANCE FROM SOMEONE WHO WORKS <span className="text-[#FE0000]">INSIDE THE PROCESS.</span>
            </h2>
            <div className="h-px w-24 bg-[#FE0000] my-8" />
          </Reveal>
          <div className="space-y-5">
            {BIO_PARAGRAPHS.map((p, i) => (
              <Reveal key={i} delay={0.08 + i * 0.05}>
                <p className="text-white text-[17px] leading-[1.8]" data-testid={`host-bio-${i}`}>{p}</p>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="grid grid-cols-2 gap-px bg-white/10 border border-white/10 rounded-lg overflow-hidden mt-10" data-testid="host-credential-grid">
              {[
                ["COMPANY", HOST.company],
                ["NMLS#", HOST.nmls],
                ["CO. NMLS#", HOST.companyNmls],
                ["LICENSED IN", HOST.states],
              ].map(([label, value]) => (
                <div key={label} className="bg-[#182f56] p-5">
                  <div className="mono text-[13px] tracking-[0.3em] text-white mb-2">{label}</div>
                  <div className="text-base font-semibold text-white">{value}</div>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.25}>
            <button
              data-testid="host-cta"
              data-cta
              onClick={() => document.getElementById("register")?.scrollIntoView({ behavior: "smooth" })}
              className="mt-10 bg-[#FFE100] text-[#12264A] font-bold text-sm uppercase tracking-wide px-8 py-4 rounded-md btn-slide"
            >
              ✓ RESERVE MY FREE SEAT
            </button>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
